import React, { useRef, useState, useEffect } from 'react';

interface ZoomableImageProps {
    id: string;
    url: string;
    alt?: string;
    title?: string;
    index: number;
    onKeyDown?: (event: React.KeyboardEvent) => void;
    onZoomChange?: (isZoomed: boolean) => void;
}

/**
 * Component for displaying an image with zoom and pan functionality
 * Uses native browser scrollbars for panning when zoomed (like Chrome)
 */
export const ZoomableImage = ({
    id,
    url,
    alt,
    title,
    index,
    onKeyDown,
    onZoomChange,
}: ZoomableImageProps) => {
    const [isZoomed, setIsZoomed] = useState(false);
    const [displayedDimensions, setDisplayedDimensions] = useState({
        width: 0,
        height: 0,
    });
    const [imageDimensions, setImageDimensions] = useState({
        width: 0,
        height: 0,
    });
    const imageRef = useRef<HTMLImageElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    // Calculate zoom scale based on image natural size vs displayed size
    const getZoomScale = (): number => {
        if (!isZoomed || imageDimensions.width === 0) return 1;

        const naturalHeight = imageDimensions.height;
        const displayedHeight = displayedDimensions.height;

        // If natural size is larger than displayed, show at natural size (scale = 1)
        // Otherwise, zoom to 1.1x for better detail
        if (naturalHeight > displayedHeight && displayedHeight > 0) {
            return 1;
        }
        return 1.1;
    };

    const handleImageClick = () => {
        setIsZoomed((prev) => {
            const newValue = !prev;
            onZoomChange?.(newValue);
            return newValue;
        });
    };

    const handleKeyDown = (event: React.KeyboardEvent) => {
        if (event.key === 'Escape' && isZoomed) {
            event.preventDefault();
            setIsZoomed(false);
            onZoomChange?.(false);
        }
        onKeyDown?.(event);
    };

    // Reset zoom state when image changes (id or url changes)
    useEffect(() => {
        setIsZoomed(false);
        setDisplayedDimensions({ width: 0, height: 0 });
        setImageDimensions({ width: 0, height: 0 });
        onZoomChange?.(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, url]);

    // Reset zoom when component unmounts
    useEffect(() => {
        return () => {
            setIsZoomed(false);
        };
    }, []);

    // Store image dimensions when loaded
    const handleImageLoad = () => {
        if (imageRef.current) {
            setImageDimensions({
                width: imageRef.current.naturalWidth,
                height: imageRef.current.naturalHeight,
            });
            // Store displayed dimensions before zooming
            setDisplayedDimensions({
                width: imageRef.current.width,
                height: imageRef.current.height,
            });
        }
    };

    // Scroll to center when zooming in
    useEffect(() => {
        if (
            isZoomed &&
            containerRef.current &&
            imageDimensions.width > 0 &&
            displayedDimensions.width > 0
        ) {
            const container = containerRef.current;
            const scale = getZoomScale();
            const scaledWidth = imageDimensions.width * scale;
            const scaledHeight = imageDimensions.height * scale;

            // Center the zoomed image after it renders
            const timeoutId = setTimeout(() => {
                if (container) {
                    container.scrollLeft =
                        (scaledWidth - container.clientWidth) / 2;
                    container.scrollTop =
                        (scaledHeight - container.clientHeight) / 2;
                }
            }, 0);

            return () => clearTimeout(timeoutId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isZoomed, imageDimensions, displayedDimensions]);

    const zoomScale = isZoomed ? getZoomScale() : 1;

    return (
        <>
            {isZoomed ? (
                <div
                    ref={containerRef}
                    className="fixed inset-0 z-50 cursor-zoom-out overflow-auto"
                    onKeyDown={handleKeyDown}
                    tabIndex={0}
                >
                    <div
                        className="flex min-h-full min-w-full items-center justify-center p-4"
                        style={{ maxWidth: '110vw' }}
                    >
                        <img
                            ref={imageRef}
                            src={url}
                            alt={alt || title || `Image ${index + 1}`}
                            className="object-contain"
                            style={{
                                width: `${imageDimensions.width}px`,
                                height: `${imageDimensions.height}px`,
                                transform: `scale(${zoomScale})`,
                                transformOrigin: 'center center',
                            }}
                            onLoad={handleImageLoad}
                            loading="eager"
                            draggable={false}
                            onClick={handleImageClick}
                        />
                    </div>
                </div>
            ) : (
                <div
                    className="relative flex h-full w-full cursor-zoom-in items-center justify-center"
                    onClick={handleImageClick}
                >
                    <img
                        ref={imageRef}
                        src={url}
                        alt={alt || title || `Image ${index + 1}`}
                        className="h-auto max-h-[calc(100dvh-4rem)] w-auto max-w-full object-contain"
                        onLoad={handleImageLoad}
                        loading="eager"
                        draggable={false}
                    />
                </div>
            )}
        </>
    );
};
